// Import statements
import { formatCurrency, InventoryChange } from '@bofrak-backend/shared';
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  chakra,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Thead,
  Tr,
} from '@chakra-ui/react';
import moment from 'moment';
import React from 'react';
import { FaPhoneAlt } from 'react-icons/fa';
import { TimeIcon } from '@chakra-ui/icons';
import { useSharedUiTranslation } from '../../i18n';

// Custom table header component
const CustomTh = chakra('th', {
  baseStyle: {
    borderBottom: '1px solid',
    borderColor: 'gray.300',
  },
});

// TableRow component
const TableRow: React.FC<InventoryChange> = ({
  change_quantity: adjustment,
  created_at,
  cost,
  source_name,
  processed_at,
  purchase_order_details,
}) => {
  const { t } = useSharedUiTranslation();
  const syncLabel = (() => {
    if (!processed_at) return null;
    const diffMs =
      new Date(processed_at).getTime() - new Date(created_at).getTime();
    if (diffMs <= 1000) return null;
    const createdDate = moment(created_at).format('DD.MM.YY');
    const processedDate = moment(processed_at).format('DD.MM.YY');
    if (createdDate === processedDate)
      return moment(processed_at).format('HH:mm:ss');
    return moment(processed_at).format('DD.MM.YY @ HH:mm:ss');
  })();

  return (
    <Tr fontSize={'sm'}>
      {/* Date and User */}
      <Td>
        <Box>
          <Text pt={1} fontWeight={'bold'} fontSize={'xs'}>
            {purchase_order_details?.id ?? t('Unknown').toUpperCase()}
          </Text>
          <Text fontSize={'xs'}>
            {source_name ?? t('Unknown').toUpperCase()}
          </Text>
          <Text fontSize={'xs'}>
            {moment(created_at).format('DD.MM.YY @ HH:mm')}
            {syncLabel && (
              <Text as="span" fontSize="2xs" color="red.500" ml={1}>
                <TimeIcon boxSize="10px" mr={1} /> {syncLabel}
              </Text>
            )}
          </Text>
        </Box>
      </Td>

      {/* Supplier*/}
      <Td>
        <Box>
          <Text fontSize={'sm'}>
            {purchase_order_details?.supplier.name ??
              t('Unknown').toUpperCase()}
          </Text>
          {purchase_order_details?.supplier.phone_number && (
            <Button
              size="xs"
              mt={1}
              p="0px"
              onClick={
                purchase_order_details?.supplier.phone_number
                  ? () =>
                      window.open(
                        `tel:${purchase_order_details?.supplier.phone_number}`,
                      )
                  : undefined
              }
              leftIcon={<FaPhoneAlt />}
              borderWidth="0px"
              variant="outline">
              {purchase_order_details?.supplier.phone_number}
            </Button>
          )}
        </Box>
      </Td>

      {/* Quantity */}
      <Td>
        <Text textAlign={'center'} fontSize={'xs'}>
          {adjustment}
        </Text>
      </Td>

      {/* Cost */}
      <Td>
        <Text textAlign={'center'} fontSize={'xs'}>
          {formatCurrency(cost, false)}
        </Text>
      </Td>
    </Tr>
  );
};

// InventoryHistoryTable component
export const ProductPurchaseHistoryTable: React.FC<{
  data: InventoryChange[];
}> = ({ data }) => {
  const { t } = useSharedUiTranslation();
  return data.length ? (
    <TableContainer width="full">
      <Table variant="simple" size="sm">
        <Thead>
          <Tr fontSize={'sm'}>
            <CustomTh></CustomTh>
            <CustomTh>{t('Supplier')}</CustomTh>
            <CustomTh>{t('Quantity')}</CustomTh>
            <CustomTh>{t('Cost')}</CustomTh>
          </Tr>
        </Thead>
        <Tbody>
          {data.map((row) => (
            <TableRow key={row.id} {...row} />
          ))}
        </Tbody>
      </Table>
    </TableContainer>
  ) : (
    <Alert textTransform={'capitalize'} width={'auto'} status="info">
      <AlertIcon />
      {t('No Purchase History Found')}
    </Alert>
  );
};
